import { type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import AppLayout from './components/layout/AppLayout';
import Login from './pages/auth/Login';
import ForgotPassword from './pages/auth/ForgotPassword';
import Signup from './pages/auth/Signup';
import OrgInquiry from './pages/auth/OrgInquiry';
import SelectOrg from './pages/auth/SelectOrg';
import Accounts from './pages/app/Accounts';
import AccountDetail from './pages/app/AccountDetail';
import Profile from './pages/app/Profile';
import Learn from './pages/app/Learn';
import ExecutiveAnalytics from './pages/app/ExecutiveAnalytics';
import ExecutiveAnalyticsWorkspace from './pages/app/ExecutiveAnalyticsWorkspace';
import Contacts from './pages/app/Contacts';
import TerritoryAnalytics from './pages/app/TerritoryAnalytics';
import MissionControl from './pages/app/MissionControl';
import Mavis from './pages/app/Mavis';
import MavisOpportunityDetail from './pages/app/MavisOpportunityDetail';
import MavisMeetingDetail from './pages/app/MavisMeetingDetail';
import BetaComingSoon from './components/common/BetaComingSoon';

function AppRoute({ children }: { children: ReactNode }) {
  const { user, organisation, loading, orgSelectionRequired } = useAuth();
  if (loading) return <div className="app-site flex h-screen items-center justify-center bg-app-canvas"><LoadingSpinner size="lg" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (orgSelectionRequired) return <Navigate to="/select-org" replace />;
  // App access requires organisation membership. Org-less users are routed to the workspace
  // selector, which renders the "no access" state and a sign-out affordance.
  if (!organisation) return <Navigate to="/select-org" replace />;
  return <>{children}</>;
}

function AuthRoute({ children }: { children: ReactNode }) {
  const { user, organisation, loading, orgSelectionRequired } = useAuth();
  if (loading) return <div className="auth-site flex h-screen items-center justify-center bg-app-canvas"><LoadingSpinner size="lg" /></div>;
  if (user && !orgSelectionRequired && organisation) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

function OrgSelectRoute({ children }: { children: ReactNode }) {
  const { user, loading, orgSelectionRequired, organisation } = useAuth();
  if (loading) return <div className="auth-site flex h-screen items-center justify-center bg-app-canvas"><LoadingSpinner size="lg" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!orgSelectionRequired && organisation) {
    return <Navigate to="/app" replace />;
  }
  return <>{children}</>;
}

function BetaRoute({ children, featureName, description }: { children: ReactNode; featureName: string; description: string }) {
  const { organisation } = useAuth();
  if (!organisation?.beta_features_enabled) {
    return <BetaComingSoon featureName={featureName} description={description} />;
  }
  return <>{children}</>;
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

function MainAppRoutes() {
  return (
    <AuthProvider>
      <SentryRoutes>
        {/* The public marketing site lives in its own deployable (see /landing).
            This application starts at the login gate. */}
        <Route path="/" element={<Navigate to="/login" replace />} />

        <Route path="/login" element={<AuthRoute><Login /></AuthRoute>} />
        <Route path="/forgot-password" element={<AuthRoute><ForgotPassword /></AuthRoute>} />
        <Route path="/signup" element={<AuthRoute><Signup /></AuthRoute>} />
        <Route path="/signup/organisation" element={<OrgInquiry />} />
        <Route path="/select-org" element={<OrgSelectRoute><SelectOrg /></OrgSelectRoute>} />

        <Route path="/app" element={<AppRoute><AppLayout /></AppRoute>}>
          <Route index element={<Accounts />} />
          <Route path="accounts/:id" element={<AccountDetail />} />
          <Route path="contacts" element={<BetaRoute featureName="Contacts Hub" description="Centralised contact management for BDRs, SDRs and AEs. Upload contacts, generate messaging, log activities and collaborate with your team across all accounts."><Contacts /></BetaRoute>} />
          <Route path="executive-analytics" element={<BetaRoute featureName="Executive Analytics" description="Build investment-grade business cases with evidence-led financial modelling, scenario analysis and executive-ready reports."><ExecutiveAnalytics /></BetaRoute>} />
          <Route path="executive-analytics/:accountId" element={<BetaRoute featureName="Executive Analytics" description="Build investment-grade business cases with evidence-led financial modelling, scenario analysis and executive-ready reports."><ExecutiveAnalyticsWorkspace /></BetaRoute>} />
          <Route path="territory-analytics" element={<BetaRoute featureName="Territory Analytics" description="Territory intelligence, team performance analytics, and account stack-ranking for sales leaders and executives."><TerritoryAnalytics /></BetaRoute>} />
          <Route path="mission-control" element={<BetaRoute featureName="Mission Control" description="Set weekly prospecting objectives, track account and contact targets, and review progress with your leader."><MissionControl /></BetaRoute>} />
          <Route path="mavis" element={<BetaRoute featureName="MAVIS" description="AI deal coach for opportunities: meeting capture with live transcripts, qualification insights, tasks and forecast assessment."><Mavis /></BetaRoute>} />
          <Route path="mavis/:opportunityId" element={<BetaRoute featureName="MAVIS" description="AI deal coach for opportunities: meeting capture with live transcripts, qualification insights, tasks and forecast assessment."><MavisOpportunityDetail /></BetaRoute>} />
          <Route path="mavis/:opportunityId/meetings/:meetingId" element={<BetaRoute featureName="MAVIS" description="AI deal coach for opportunities: meeting capture with live transcripts, qualification insights, tasks and forecast assessment."><MavisMeetingDetail /></BetaRoute>} />
          <Route path="learn/*" element={<Learn />} />
          <Route path="profile" element={<Profile />} />
        </Route>

        <Route path="/create-org" element={<Navigate to="/signup" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </SentryRoutes>
    </AuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <MainAppRoutes />
    </BrowserRouter>
  );
}
